import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { queryClient } from "App";
import ACL_RELATIONSHIPS from "acl-relationships";
import tagConstants from "constants/Tag";
import useAuthorized from "hooks/useAuthorized";
import {
  getLeadsTableColumns,
  getLeadsTableFilters,
} from "routes/leads/table/LeadsTableColumnsAndFilters";
import { LEADS_TABLE_CONSTANTS } from "routes/leads/table/LeadsTableV3";
import LeadService from "services/Lead";
import MetaLeadsEventService, {
  META_LEADS_EVENTS_QUERY_KEY,
} from "services/MetaLeadsEvent";
import TagService from "services/Tag";
import { leadKeys } from "routes/leads/table/queryKeys";

const fetchLeadTags = () => {
  const { taggableModels } = tagConstants;
  const model = Object.keys(taggableModels).find(
    key => taggableModels[key] === LEADS_TABLE_CONSTANTS.className
  );
  return TagService.getTags({ model, for_filtering: true });
};

export const useLeadsTable = ({ scope, id }) => {
  const queryKey = useMemo(() => leadKeys.list({ scope, id }), [scope, id]);

  const enableTags = useAuthorized(
    [
      ACL_RELATIONSHIPS.leadTags.create,
      ACL_RELATIONSHIPS.leadTags.read,
      ACL_RELATIONSHIPS.leadTags.delete,
    ],
    { partialMatch: true }
  );

  const { data: events } = useQuery({
    queryKey: [META_LEADS_EVENTS_QUERY_KEY],
    queryFn: () => MetaLeadsEventService.list(),
  });

  const { data: tags } = useQuery({
    queryKey: ["tags", LEADS_TABLE_CONSTANTS.tableName],
    queryFn: fetchLeadTags,
  });

  const invalidateData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey });
  }, [queryKey]);

  const updateStatus = useCallback(
    async (leadId, statusId) => {
      await LeadService.updateStatus(scope, id, leadId, statusId);
      await invalidateData();
    },
    [scope, id, invalidateData]
  );

  const deleteLead = useCallback(
    async leadId => {
      await LeadService.deleteLead(scope, id, leadId);
      await invalidateData();
    },
    [scope, id, invalidateData]
  );

  const bulkUpdateStatus = useCallback(
    async (leadIds, statusId) => {
      await LeadService.bulkUpdateStatus(scope, id, leadIds, statusId);
      await invalidateData();
    },
    [scope, id, invalidateData]
  );

  const filters = useMemo(
    () => getLeadsTableFilters({ events, tags }),
    [events, tags]
  );

  const columns = useMemo(
    () =>
      getLeadsTableColumns({
        scope,
        events,
        updateStatus,
        deleteLead,
        invalidateData,
      }),
    [scope, events, updateStatus, deleteLead, invalidateData]
  );

  return {
    filters,
    columns,
    events: events ?? [],
    queryKey,
    invalidateData,
    bulkUpdateStatus,
    enableTags,
  };
};
